// src/Sidebar.js
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { navGroups } from './navItems';
import usePermissions from './usePermissions';
import useSidebarCollapsed from './useSidebarCollapsed';

import { brand, surface } from './designTokens';


export default function Sidebar({ activeTab, setActiveTab }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const aboveAppBar = theme.zIndex.appBar + 1;
  const { hasPermission, isSuperAdmin, loading } = usePermissions();
  const { collapsed, toggle, width: drawerWidth } = useSidebarCollapsed();

  // Track which groups are open
  const [openGroups, setOpenGroups] = useState(() => {
    const initialState = {};
    navGroups.forEach((group, idx) => {
      if (group.collapsible) {
        initialState[idx] = group.defaultOpen !== false; // Default to true if not specified
      }
    });
    return initialState;
  });

  const handleNav = (item, groupIdx) => {
    setActiveTab(item.table);
    navigate(item.path);
    // Auto-expand the group if it's collapsed
    if (groupIdx !== undefined && navGroups[groupIdx]?.collapsible && !openGroups[groupIdx]) {
      setOpenGroups(prev => ({
        ...prev,
        [groupIdx]: true
      }));
    }
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        zIndex: aboveAppBar,
        transition: 'width .2s ease',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          // Matches the header margin's transition in App.js, or the two slide
          // apart and a gap opens for a fifth of a second.
          transition: 'width .2s ease',
          boxSizing: 'border-box',
          backgroundColor: surface.sunken,
          borderRight: 'none',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible'
        },
      }}
    >
      {/* The logo is the collapse control. A chevron beside it pushed the mark
          off centre to make room for itself, and the sidebar only has two
          states, so the logo can carry both. */}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: collapsed ? 1.5 : 2.5 }}>
        <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right" arrow>
          <IconButton
            onClick={toggle}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-expanded={!collapsed}
            sx={{
              p: 0.75,
              borderRadius: 3,
              transition: 'transform 160ms ease',
              '&:hover': { backgroundColor: 'transparent', transform: 'scale(1.06)' },
            }}
          >
            <Box
              component="img"
              src="/app-icon.png"
              alt="LIVLIFE"
              sx={{
                display: 'block',
                width: collapsed ? 40 : 64,
                height: collapsed ? 40 : 64,
                borderRadius: collapsed ? '10px' : '16px',
                transition: 'width 160ms ease, height 160ms ease, border-radius 160ms ease',
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Nav items grouped with collapsible sections */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pt: 1 }}>
          {navGroups.map((group, groupIdx) => {
            // Filter items based on permissions
            const visibleItems = group.items.filter(item => {
              // Dashboard is always visible
              if (item.table === 'Home') return true;
              // While permissions are loading, show all items (for better UX)
              if (loading) return true;
              // Super admins see everything
              if (isSuperAdmin) return true;
              // If no permission specified, show it (for backward compatibility)
              if (!item.permission) return true;
              // If item has permission requirement, check it
              return hasPermission(item.permission);
            });

            // Don't show group if no items are visible
            if (visibleItems.length === 0) return null;

            // Non-collapsible groups (like Dashboard)
            if (!group.collapsible || !group.label) {
              return (
                <Box key={groupIdx} sx={{ mb: 1 }}>
                  <List dense>
                    {visibleItems.map(item => (
                      <ListItem
                        key={item.table}
                        button
                        title={collapsed ? item.label : undefined}
                        selected={activeTab === item.table}
                        onClick={() => handleNav(item, groupIdx)}
                        sx={{
                          py: 0.5,
                          px: collapsed ? 1.5 : 2,
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          mb: 0.25,
                          borderRadius: 1,
                          mx: 1,
                          '&.Mui-selected': {
                            backgroundColor: brand.goldWash,
                            '&:hover': {
                              backgroundColor: brand.goldWash,
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: activeTab === item.table ? brand.gold : '#555',
                            minWidth: collapsed ? 0 : 32,
                            '& svg': { fontSize: '1rem' }
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {!collapsed && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                            color: activeTab === item.table ? brand.gold : '#333',
                            fontWeight: activeTab === item.table ? 600 : 400,
                          }}
                            sx={{ ml: 0 }}
                          />
                          )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            }

            // Collapsible groups - keeping original structure but need to add back collapsible functionality
            // For now, just show items without collapse
            return (
              <Box key={groupIdx} sx={{ mb: 0.5 }}>
                {collapsed ? (
                  <Divider sx={{ mx: 1.5, my: 1 }} />
                ) : (
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#666',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {group.label}
                </Typography>
                )}
                <List dense component="div" disablePadding>
                  {visibleItems.map(item => (
                    <ListItem
                      key={item.table}
                      button
                      title={collapsed ? item.label : undefined}
                      selected={activeTab === item.table}
                      onClick={() => handleNav(item, groupIdx)}
                      sx={{
                        py: 0.5,
                        px: collapsed ? 1.5 : 3,
                          justifyContent: collapsed ? 'center' : 'flex-start',
                        mb: 0.25,
                        borderRadius: 1,
                        mx: 1,
                        '&.Mui-selected': {
                          backgroundColor: brand.goldWash,
                          '&:hover': {
                            backgroundColor: brand.goldWash,
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: activeTab === item.table ? brand.gold : '#555',
                          minWidth: collapsed ? 0 : 32,
                          '& svg': { fontSize: '0.9rem' }
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                          color: activeTab === item.table ? brand.gold : '#333',
                          fontWeight: activeTab === item.table ? 600 : 400,
                        }}
                          sx={{ ml: 0 }}
                        />
                        )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })}
      </Box>
    </Drawer>
  );
}
